import { useMemo } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import type { UseQueryOptions } from '@tanstack/react-query'
import type { BestTimeCell, Brand, Provider } from '@anubis-connector/shared'
import { LAUNCH_PROVIDERS } from '@anubis-connector/shared'

import { mcApi } from '../lib/api'
import { qk } from '../lib/query'
import { dayjs, toUpstreamDateTime } from '../lib/format'

/**
 * `GET /api/v2/scheduler/besttimes/{provider}` — shape copied from the swagger
 * (`JsonOkListResponseBestTimes`). Declared here because the frontend consumes the
 * compat namespace directly; the generated spec types belong to the backend agent.
 */
interface BestTimesByHourWire {
  hourOfDay?: number
  value?: number
}
interface BestTimesWire {
  dayOfWeek?: number
  bestTimesByHour?: BestTimesByHourWire[]
}
interface BestTimesResponse {
  data?: BestTimesWire[]
}

export interface AudienceActivity {
  /** 7 × 24 cells, score normalised 0–1. Feeds the best-times heatmap. */
  grid: BestTimeCell[]
  /** 24 values, 0–1 — the Now Rail density row. */
  hourly: number[]
  /** The hour with the highest average score, or `null` when there is no signal. */
  peakHour: number | null
}

export const EMPTY_ACTIVITY: AudienceActivity = { grid: [], hourly: Array(24).fill(0) as number[], peakHour: null }

/** Upstream weekday is 1 = Monday; `BestTimeCell.weekday` is 0 = Monday. */
const normaliseWeekday = (dayOfWeek: number): number => ((dayOfWeek - 1) % 7 + 7) % 7

const aggregate = (payload: BestTimesResponse | null): AudienceActivity => {
  const rows = payload?.data ?? []
  if (rows.length === 0) return EMPTY_ACTIVITY

  const raw: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0) as number[])
  let max = 0

  for (const row of rows) {
    if (typeof row.dayOfWeek !== 'number') continue
    const weekday = normaliseWeekday(row.dayOfWeek)
    const lane = raw[weekday]
    if (!lane) continue
    for (const cell of row.bestTimesByHour ?? []) {
      const hour = cell.hourOfDay
      const value = cell.value
      if (typeof hour !== 'number' || hour < 0 || hour > 23) continue
      if (typeof value !== 'number' || !Number.isFinite(value)) continue
      lane[hour] = value
      if (value > max) max = value
    }
  }

  if (max <= 0) return EMPTY_ACTIVITY

  const grid: BestTimeCell[] = []
  const hourly = Array(24).fill(0) as number[]

  for (let weekday = 0; weekday < 7; weekday += 1) {
    const lane = raw[weekday]
    if (!lane) continue
    for (let hour = 0; hour < 24; hour += 1) {
      const score = (lane[hour] ?? 0) / max
      grid.push({ weekday, hour, score })
      hourly[hour] = (hourly[hour] ?? 0) + score / 7
    }
  }

  let peakHour = 0
  for (let hour = 1; hour < 24; hour += 1) {
    if ((hourly[hour] ?? 0) > (hourly[peakHour] ?? 0)) peakHour = hour
  }

  return { grid, hourly, peakHour }
}

/** The network whose audience profile represents the brand: first connected launch network. */
export const primaryProvider = (brand: Brand | null): Provider | null => {
  if (!brand) return null
  for (const provider of LAUNCH_PROVIDERS) {
    const match = brand.connections.find(
      (connection) => connection.provider === provider && connection.status !== 'EXPIRED',
    )
    if (match) return match.provider
  }
  return brand.connections[0]?.provider ?? null
}

/** Every network this brand has live right now, `LAUNCH_PROVIDERS`-ordered — the Now Rail's
 *  network picker lists exactly these (plus "All networks"), same connected/`EXPIRED` split
 *  `primaryProvider` already draws. */
export const connectedProviders = (brand: Brand | null): Provider[] => {
  if (!brand) return []
  return LAUNCH_PROVIDERS.filter((provider) =>
    brand.connections.some((connection) => connection.provider === provider && connection.status !== 'EXPIRED'),
  )
}

export interface UseBestTimesResult {
  activity: AudienceActivity
  status: 'pending' | 'error' | 'success'
  error: unknown
  /** No connection to read a profile from — render the scale without a density row. */
  idle: boolean
}

/**
 * How much history the profile is measured over. Four weeks is long enough for every
 * weekday to appear several times and short enough that a brand that changed its posting
 * rhythm is not judged on what it used to do.
 *
 * The parameters are `start` / `end` because that is what the swagger declares for
 * `/v2/scheduler/besttimes/{provider}`; the backend read `from` / `to` and so ignored this
 * window entirely, answering its 180-day default to every caller. It accepts both spellings
 * now, and this hook keeps the ones a real Metricool client would send.
 */
const WINDOW_DAYS = 28

/** One provider's query, factored out so `useBestTimes` (one network) and
 *  `useBestTimesForProviders` (every connected network, for the Now Rail's "All networks") ask
 *  the same question the same way — a single `useQueries` call rather than a hook invoked in a
 *  loop, which would violate the rules of hooks the moment a brand's connection count changes. */
const bestTimesQueryOptions = (
  blogId: number,
  provider: Provider,
  timezone: string,
): UseQueryOptions<BestTimesResponse> => ({
  queryKey: qk.bestTimes(blogId, provider),
  staleTime: 30 * 60_000,
  queryFn: () => {
    // The window is stamped in the brand's own timezone, the same one sent as `timezone`.
    // Formatting it in the browser's zone instead shifted both ends by the offset between
    // the two, so a reader in another country asked for a window nobody meant.
    const end = dayjs()
    return mcApi<BestTimesResponse>(`/v2/scheduler/besttimes/${provider}`, {
      start: toUpstreamDateTime(end.subtract(WINDOW_DAYS, 'day'), timezone),
      end: toUpstreamDateTime(end, timezone),
      timezone,
      blogId,
    })
  },
})

/**
 * The brand's audience-activity profile for one network, aggregated to a 7 × 24 grid
 * and to a 24-hour row. Shared by the Now Rail and the best-times heatmap.
 */
export const useBestTimes = (
  blogId: number | null,
  provider: Provider | null,
  timezone: string,
): UseBestTimesResult => {
  const enabled = blogId !== null && provider !== null

  const query = useQuery({
    ...bestTimesQueryOptions(blogId ?? 0, provider ?? 'facebook', timezone),
    enabled,
  })

  const activity = useMemo(() => aggregate(query.data ?? null), [query.data])

  return {
    activity,
    status: query.status,
    error: query.error,
    idle: !enabled,
  }
}

/** Every activity's own 0–1 normalisation already makes it "how intense, relative to this
 *  network's own busiest hour" — averaging those across networks (skipping any with no signal
 *  at all) gives a fair blended profile without needing to compare raw cross-network magnitudes. */
const combineActivity = (activities: readonly AudienceActivity[]): AudienceActivity => {
  const withSignal = activities.filter((activity) => activity.grid.length > 0)
  if (withSignal.length === 0) return EMPTY_ACTIVITY

  const hourly = Array(24).fill(0) as number[]
  for (const activity of withSignal) {
    for (let hour = 0; hour < 24; hour += 1) {
      hourly[hour] = (hourly[hour] ?? 0) + (activity.hourly[hour] ?? 0) / withSignal.length
    }
  }

  const scoreByCell = new Map<string, number>()
  for (const activity of withSignal) {
    for (const cell of activity.grid) {
      const key = `${cell.weekday}-${cell.hour}`
      scoreByCell.set(key, (scoreByCell.get(key) ?? 0) + cell.score / withSignal.length)
    }
  }
  const grid: BestTimeCell[] = Array.from(scoreByCell.entries()).map(([key, score]) => {
    const [weekday, hour] = key.split('-').map(Number) as [number, number]
    return { weekday, hour, score }
  })

  let peakHour = 0
  for (let hour = 1; hour < 24; hour += 1) {
    if ((hourly[hour] ?? 0) > (hourly[peakHour] ?? 0)) peakHour = hour
  }

  return { grid, hourly, peakHour }
}

export interface UseBestTimesForProvidersResult {
  /** One entry per requested provider, in the same order. */
  activityByProvider: ReadonlyMap<Provider, AudienceActivity>
  /** Every provider's activity, blended — the Now Rail's "All networks". */
  combined: AudienceActivity
  status: 'pending' | 'error' | 'success'
  /** No providers to ask about — render the scale without a density row. */
  idle: boolean
}

/**
 * The same profile `useBestTimes` fetches, for every network in `providers` at once — one
 * `useQueries` call, so switching the Now Rail's network picker is a pure client-side
 * recombination of data already in hand, not a new round trip.
 */
export const useBestTimesForProviders = (
  blogId: number | null,
  providers: readonly Provider[],
  timezone: string,
): UseBestTimesForProvidersResult => {
  const enabled = blogId !== null && providers.length > 0

  const queries = useQueries({
    queries: providers.map((provider) => ({
      ...bestTimesQueryOptions(blogId ?? 0, provider, timezone),
      enabled,
    })),
  })

  const activityByProvider = useMemo(() => {
    const out = new Map<Provider, AudienceActivity>()
    providers.forEach((provider, index) => {
      out.set(provider, aggregate(queries[index]?.data ?? null))
    })
    return out
  }, [providers, queries])

  const combined = useMemo(
    () => combineActivity(Array.from(activityByProvider.values())),
    [activityByProvider],
  )

  const status: 'pending' | 'error' | 'success' = queries.some((query) => query.status === 'error')
    ? 'error'
    : queries.some((query) => query.status === 'pending')
      ? 'pending'
      : 'success'

  return { activityByProvider, combined, status, idle: !enabled }
}
